// customTrailAsTrail — wrapper der eine device-lokale CustomTrail in eine
// CachedTrail-kompatible Hülle überführt. Analog zu runAsTrail.ts, damit die
// MapPage einen Custom-Trail ohne Spezial-Pfad rendern kann (Aura,
// ElevationProfile, RIDE-Button, Navigate-to-start).
//
// Wave 14bv Stage 6m: "wenn ich einen custom trail in der liste anklicke,
// soll er in der MAP ansicht öffnen und ab dann soll es sich so verhalten
// als hätte ich in der MAP ansicht einfach auf einen trail geklickt".
//
// ID-Prefix `custom:` ist Vertrag: echte Trail-IDs sind Slugs/UUIDs ohne
// Doppelpunkt; `run:` ist für DH-Runs reserviert. `custom:` kollidiert also
// mit nichts. MapPage-Komponenten, die bei Pseudo-IDs aussteigen (z.B.
// AddCruxHere, RunsForTrail), erkennen das analog zu isRunPseudoId.
//
// Höhenprofil: CustomTrail speichert keine Elevations (der Save passiert
// aus CreateCustomPage heraus, die nur Route-Coords hat). useTrailElevation
// rechnet daher beim ersten Öffnen per SRTM/DEM-Sampling und cached's in
// IDB unter dem Pseudo-ID. Kein Backend-Roundtrip nötig.

import type { GpxTrack } from '@adtu/shared';
import { trackLength, elevationStats } from '@adtu/shared';
import type { CachedTrail } from './trails';
import type { CustomTrail } from './customTrails';

export const CUSTOM_TRAIL_ID_PREFIX = 'custom:';

export function isCustomTrailPseudoId(
  id: string | null | undefined,
): boolean {
  return typeof id === 'string' && id.startsWith(CUSTOM_TRAIL_ID_PREFIX);
}

export function extractCustomTrailId(pseudoId: string): string {
  if (!isCustomTrailPseudoId(pseudoId)) return '';
  return pseudoId.slice(CUSTOM_TRAIL_ID_PREFIX.length);
}

/**
 * Baut aus einer CustomTrail eine CachedTrail-Hülle.
 *
 * Wave 14cf-fix5: CustomTrail.coords kann ein optionales drittes Element
 * (Elevation in Metern) enthalten. Wenn vorhanden, geben wir's an die
 * GpxPoint.ele weiter — useTrailElevation erkennt das via hasElevationData()
 * und überspringt den SRTM-Fallback. Das ist deutlich akkurater wenn die
 * Quelle akkurate Höhen liefert (z.B. Komoot-GPX-Import).
 *
 * Alte CustomTrails (gespeichert als 2-Tupel [lon, lat]) funktionieren
 * weiterhin — c[2] ist dann undefined, GpxPoint.ele wird weggelassen, und
 * der SRTM-Pfad greift wie bisher.
 */
// Prefix für flüchtige (nicht gespeicherte) Routen — z.B. „RIDE" direkt aus
// dem Pit-Stop-Voraus-Flow, ohne als Custom-Tour zu persistieren. Eigener
// Prefix, damit nichts versucht, sie per getCustomTrail aus IDB zu laden.
export const EPHEMERAL_TRAIL_ID_PREFIX = 'ephemeral:';

export function isEphemeralTrailPseudoId(
  id: string | null | undefined,
): boolean {
  return typeof id === 'string' && id.startsWith(EPHEMERAL_TRAIL_ID_PREFIX);
}

/**
 * Baut eine CachedTrail-Hülle direkt aus Routen-Koordinaten — OHNE Speichern.
 * Für „nur fahren, nicht speichern" (Ultra-Rennen: Navigation zu einem POI).
 * coords = [lon, lat] oder [lon, lat, ele].
 */
export function ephemeralRouteAsTrail(opts: {
  name: string;
  coords: Array<[number, number] | [number, number, number]>;
}): CachedTrail {
  const points = opts.coords.map((c) => {
    const lon = c[0];
    const lat = c[1];
    const ele = (c as [number, number, number])[2];
    if (typeof ele === 'number' && Number.isFinite(ele)) return { lat, lon, ele };
    return { lat, lon };
  });
  const track: GpxTrack = { points };
  return {
    id: `${EPHEMERAL_TRAIL_ID_PREFIX}${Date.now()}`,
    name: opts.name,
    difficulty: 'unknown',
    trailType: 'Route',
    colorHex: '#38bdf8',
    description: null,
    photos: [],
    gpxFilename: '',
    track,
    cruxPoints: [],
    stats: {
      lengthMeters: trackLength(points),
      ...elevationStats(points, { deadbandMeters: 2 }),
    },
    cachedAt: new Date().toISOString(),
  };
}

export function customTrailAsTrail(ct: CustomTrail): CachedTrail {
  // CustomTrail.coords ist [lon, lat] oder [lon, lat, ele] — GpxPoint hat
  // {lat, lon, ele?}. ele wird nur gesetzt wenn finite Zahl.
  const points = ct.coords.map((c) => {
    const lon = c[0];
    const lat = c[1];
    const ele = (c as [number, number, number])[2];
    if (typeof ele === 'number' && Number.isFinite(ele)) {
      return { lat, lon, ele };
    }
    return { lat, lon };
  });
  const track: GpxTrack = { points };
  const stats = {
    lengthMeters: trackLength(points),
    // Wenn ele in coords drin ist, liefert elevationStats schon brauchbare
    // gain/loss-Werte aus den GPX-Höhen — das ersetzt zwar nicht das
    // hysterese-basierte derivedStats im Profil-Widget (siehe MapPage),
    // ist aber ein sinnvoller Fallback wenn die Profil-Berechnung noch
    // pending ist.
    ...elevationStats(points, { deadbandMeters: 2 }),
  };
  return {
    id: `${CUSTOM_TRAIL_ID_PREFIX}${ct.id}`,
    name: ct.name,
    // Custom-Trails haben aktuell keine Difficulty-Zuordnung beim Save
    // (der Create-Custom-Flow kennt die nicht). `unknown` → graue Farbe
    // + kein Trail-Type-Label im Sheet.
    difficulty: 'unknown',
    // Eigenes trailType-Label wird im Detail-Sheet als "Custom route"
    // angezeigt.
    trailType: 'Custom route',
    // Sky-400 matcht den Look-and-Feel der Create-Custom-Page-Linie.
    colorHex: '#38bdf8',
    description: null,
    photos: [],
    gpxFilename: '',
    track,
    cruxPoints: [],
    stats,
    cachedAt: ct.createdAt,
    // Wave 14ca-fix64: routeSegments-Mapping plumbing fuer Trail-Badges +
    // Höhenprofil-Faerbung im Detail-Sheet.
    routeSegments: ct.routeSegments,
    // Wave 14ca-fix64-v3: User-Auswahl beim Planen mitschleppen — wird als
    // Whitelist genutzt damit Trails die zufaellig nahe der Route liegen
    // (oder von der Routing-Engine traversiert wurden ohne dass der User
    // sie angetappt hat) NICHT in der Trail-Liste / Profil-Faerbung
    // auftauchen. Fallback auf trailsUsed wenn trailIds nicht persistiert
    // ist (alte Tours / Stroke-Routes).
    selectedTrailIds:
      ct.trailIds && ct.trailIds.length > 0
        ? ct.trailIds
        : ct.trailsUsed && ct.trailsUsed.length > 0
          ? ct.trailsUsed
          : undefined,
  };
}
